import { FC } from 'react'
import { Alert, AlertTitle, Box, Link, Typography } from '@mui/material'
import ButtonComp from 'src/components/Button/Button'
import ConfirmModal from 'src/components/ConfirmModal/ConfirmModal'
import {
  ResCenterMigrationBannerState,
  TryItOutcome,
  getForgotPasswordUrl,
  isBannerVisible,
  isTryItUnlocked,
  shouldRenderBannerView,
} from 'src/helpers/resCenterMigrationBanner'
import { RES_CENTER_MIGRATION_COPY as copy } from './copy'

export type ResCenterMigrationBannerViewProps = {
  state?: ResCenterMigrationBannerState | null
  confirmOpen: boolean
  submitting: boolean
  learnMoreFailed: boolean
  outcome: TryItOutcome | null
  onLearnMore: () => void
  onTryIt: () => void
  onCancel: () => void
  onConfirm: () => void
}

export const ResCenterMigrationBannerView: FC<
  ResCenterMigrationBannerViewProps
> = ({
  state,
  confirmOpen,
  submitting,
  learnMoreFailed,
  outcome,
  onLearnMore,
  onTryIt,
  onCancel,
  onConfirm,
}) => {
  const bannerVisible = isBannerVisible(state)
  const emailed = outcome?.kind === 'emailed' ? outcome : null
  const error = outcome?.kind === 'error' ? outcome : null
  const forgotPasswordUrl = getForgotPasswordUrl(state)
  if (!shouldRenderBannerView({ state, outcome, learnMoreFailed })) return null

  return (
    <>
      {bannerVisible && (
        <Alert
          severity="info"
          sx={{
            borderRadius: 0,
            color: 'text.primary',
            backgroundColor: 'info.light',
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            '& .MuiAlert-message': { flex: 1 },
            '& .MuiAlert-action': {
              flexBasis: { xs: '100%', md: 'auto' },
              justifyContent: { xs: 'flex-start', md: 'flex-end' },
              ml: { xs: 6.25, md: 2 },
              mr: 0,
              pt: { xs: 0, md: 0.5 },
            },
          }}
          action={
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <ButtonComp
                variant="outlined"
                onClick={onLearnMore}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {copy.banner.learnMore}
              </ButtonComp>
              {isTryItUnlocked(state) && (
                <ButtonComp
                  variant="contained"
                  disabled={submitting}
                  onClick={onTryIt}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {submitting ? copy.banner.sending : copy.banner.tryIt}
                </ButtonComp>
              )}
            </Box>
          }
        >
          <AlertTitle>{copy.banner.title}</AlertTitle>
          {copy.banner.body}
        </Alert>
      )}
      {learnMoreFailed && (
        <Alert severity="warning" style={{ borderRadius: 0 }}>
          {copy.learnMoreError}
        </Alert>
      )}
      {emailed && (
        <Alert
          severity="success"
          sx={{
            borderRadius: 0,
            color: 'text.primary',
            backgroundColor: 'success.light',
            '& .MuiAlert-icon': { color: 'success.dark' },
            '& .MuiAlert-message': { width: '100%' },
          }}
        >
          <AlertTitle sx={{ fontWeight: 600, mb: 0.5 }}>
            {copy.emailed.title}
          </AlertTitle>
          <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
            {emailed.status === 'already_sent'
              ? copy.emailed.alreadySent
              : copy.emailed.body}
          </Typography>
          {forgotPasswordUrl && (
            <Box sx={{ mt: 1.25 }}>
              <Link
                href={forgotPasswordUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontWeight: 600 }}
              >
                {copy.emailed.forgotPassword}
              </Link>
            </Box>
          )}
        </Alert>
      )}
      {error && (
        <Alert severity="error" style={{ borderRadius: 0 }}>
          {copy.errors[error.errorKey]}
        </Alert>
      )}
      {bannerVisible && (
        <ConfirmModal
          id="res-center-migration-try-it"
          show={confirmOpen}
          title={copy.confirm.title}
          description={copy.confirm.body}
          confrimText={copy.confirm.confirm}
          cancelText={copy.confirm.cancel}
          handleClose={onCancel}
          handleConfirm={onConfirm}
        />
      )}
    </>
  )
}
